import { useMemo } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

export interface FundHouseOption { id: string; name: string }

interface Props {
  options: FundHouseOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  label?: string;
  className?: string;
}

export default function FundHousesMultiSelect({ options, selected, onChange, label, className }: Props) {
  const allIds = useMemo(() => options.map((o) => o.id), [options]);
  const allSelected = selected.length === options.length && options.length > 0;
  const summary = options.length === 0
    ? "No fund houses"
    : allSelected
    ? `All (${options.length})`
    : `${selected.length} of ${options.length}`;

  const toggle = (id: string) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn("h-7 text-[11px] gap-1 font-normal", className)}>
          <span className="truncate max-w-[120px]">{label ?? summary}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="end">
        <div className="flex items-center justify-between px-2 py-1 text-xs text-muted-foreground">
          <span>{summary}</span>
          <div className="flex gap-2">
            <button className="hover:underline" onClick={() => onChange(allIds)} type="button">All</button>
            <button className="hover:underline" onClick={() => onChange([])} type="button">None</button>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1 mt-1">
          {options.map((o) => {
            const checked = selected.includes(o.id);
            return (
              <label key={o.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted/50 cursor-pointer text-xs">
                <Checkbox checked={checked} onCheckedChange={() => toggle(o.id)} />
                <span className="flex-1 truncate">{o.name}</span>
                {checked && <Check className="h-3 w-3 text-primary" />}
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
